import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ScanLine, Newspaper, Gauge, BookOpen, TrendingUp, Check, ArrowRight, Eye, Zap } from "lucide-react";
import api from "@/lib/api";
import Disclaimer from "@/components/Disclaimer";
import { ThemeToggle } from "@/components/ThemeToggle";

const tickers = [
  { s: "AAPL", p: "228.14", c: "+1.24%" }, { s: "TSLA", p: "251.44", c: "-0.86%" },
  { s: "BTC", p: "67,204", c: "+2.15%" }, { s: "NVDA", p: "134.81", c: "+3.02%" },
  { s: "SPY", p: "571.90", c: "+0.42%" }, { s: "ETH", p: "2,610", c: "-1.10%" },
  { s: "MSFT", p: "421.33", c: "+0.68%" }, { s: "EURUSD", p: "1.0842", c: "+0.11%" },
];

const features = [
  { icon: ScanLine, title: "Live Screen Vision + OCR", desc: "Share your trading app screen. Our CV engine reads charts, prices, and indicators in real time." },
  { icon: Gauge, title: "Win/Loss Probability", desc: "Every signal comes with a quantified buy vs. sell win-rate percentage — no vague opinions." },
  { icon: Newspaper, title: "Live News & Sentiment", desc: "The AI fuses breaking company news and market data into every recommendation." },
  { icon: BookOpen, title: "Smart Trade Journal", desc: "Log entries, exits, strategy and emotion. Auto-computed P&L and outcome tagging." },
  { icon: TrendingUp, title: "Performance Analytics", desc: "Equity curves, win rate, best/worst assets, and risk-reward distribution." },
  { icon: Zap, title: "Actionable Guidance", desc: "Precise entry, stop-loss and take-profit levels the moment you need them." },
];

export default function Landing() {
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get("/config").then(({ data }) => setConfig(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#07090E] text-white overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
            <div className="relative">
              <Activity className="w-7 h-7 text-emerald-500" />
              <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-emerald-400 rounded-full pulse-dot" />
            </div>
            <span className="font-display font-extrabold text-lg tracking-tight">AI Trading Copilot</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-slate-300">
            <a href="#features" className="hover:text-emerald-400 transition-colors" data-testid="nav-features-link">Features</a>
            <a href="#how" className="hover:text-emerald-400 transition-colors">How it works</a>
            <a href="#pricing" className="hover:text-emerald-400 transition-colors" data-testid="nav-pricing-link">Pricing</a>
          </nav>
          <div className="flex items-center gap-3">
            <ThemeToggle compact />
            <Link to="/login" data-testid="nav-login-button" className="text-sm text-slate-300 hover:text-white transition-colors">Log in</Link>
            <Link to="/login" data-testid="nav-start-trial-button"
              className="text-sm font-semibold bg-emerald-500 hover:bg-emerald-400 text-[#07090E] px-4 py-2 rounded-full transition-colors">
              Start Free Trial
            </Link>
          </div>
        </div>
      </header>

      {/* Ticker marquee */}
      <div className="border-b border-white/10 bg-[#0D111A] overflow-hidden">
        <div className="flex marquee whitespace-nowrap py-2">
          {[...tickers, ...tickers].map((t, i) => (
            <span key={i} className="inline-flex items-center gap-2 px-6 font-mono-num text-sm">
              <span className="text-slate-400">{t.s}</span>
              <span className="text-white">{t.p}</span>
              <span className={t.c.startsWith("+") ? "text-emerald-400" : "text-red-400"}>{t.c}</span>
            </span>
          ))}
        </div>
      </div>

      {/* Hero */}
      <section className="relative">
        <div className="absolute inset-0 hero-glow pointer-events-none" />
        <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 grid lg:grid-cols-2 gap-12 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <span className="inline-flex items-center gap-2 text-xs font-medium uppercase tracking-wider text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1 rounded-full mb-6">
              <Eye className="w-3 h-3" /> Computer Vision Trading Intelligence
            </span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-none mb-6">
              Trade with real-time <span className="text-emerald-400">vision</span> & AI precision
            </h1>
            <p className="text-slate-400 text-base sm:text-lg mb-8 max-w-lg leading-relaxed">
              Your AI Copilot watches your screen live, reads charts via computer vision, scans breaking market
              news, and delivers precise win/loss probabilities in milliseconds.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/login" data-testid="hero-start-trial-button"
                className="inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-400 text-[#07090E] font-semibold px-6 py-3 rounded-full transition-colors">
                Start {config?.trial_days || 10}-Day Free Trial <ArrowRight className="w-4 h-4" />
              </Link>
              <a href="#features" className="inline-flex items-center justify-center gap-2 border border-white/15 hover:border-white/30 text-white font-medium px-6 py-3 rounded-full transition-colors">
                See how it works
              </a>
            </div>
            <p className="text-xs text-slate-500 mt-4">
              {config?.trial_days || 10}-day full access • No credit card required • Cancel anytime
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7, delay: 0.15 }}
            className="relative">
            <div className="glass rounded-2xl p-5 relative scanline overflow-hidden">
              <img src="https://images.unsplash.com/photo-1735469157670-1212e570eadc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000"
                alt="Trading terminal" className="rounded-xl w-full h-56 object-cover mb-4" />
              <div className="grid grid-cols-2 gap-4">
                <div className="flex flex-col items-center">
                  <div className="relative w-24 h-24 rounded-full flex items-center justify-center"
                    style={{ background: "conic-gradient(#10B981 281deg, var(--gauge-track) 0deg)" }}>
                    <div className="w-[76px] h-[76px] rounded-full bg-[#0D111A] flex items-center justify-center">
                      <span className="font-mono-num text-xl font-bold text-emerald-400">78%</span>
                    </div>
                  </div>
                  <span className="text-xs text-slate-400 mt-2 uppercase tracking-wider">Buy win-rate</span>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-slate-400">Signal</span><span className="text-emerald-400 font-semibold">BUY</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Entry</span><span className="font-mono-num">228.10</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Stop</span><span className="font-mono-num text-red-400">224.50</span></div>
                  <div className="flex justify-between"><span className="text-slate-400">Target</span><span className="font-mono-num text-emerald-400">236.80</span></div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="mb-12 max-w-2xl">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-4">Everything a serious trader needs</h2>
          <p className="text-slate-400">One terminal for vision-based analysis, live intelligence, journaling, and performance tracking.</p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              className="glass rounded-xl p-6 hover:border-emerald-500/30 transition-colors group">
              <div className="w-11 h-11 rounded-lg bg-emerald-500/10 flex items-center justify-center mb-4 group-hover:bg-emerald-500/20 transition-colors">
                <f.icon className="w-5 h-5 text-emerald-400" />
              </div>
              <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { n: "01", t: "Share your screen", d: "Start a screen share of your broker or charting app — or upload a chart screenshot." },
            { n: "02", t: "AI reads & analyzes", d: "Computer vision + OCR extract price action; live news and market data are fused in." },
            { n: "03", t: "Get probabilities", d: "Receive buy/sell win-rate %, entry, stop and target. You make the final call." },
          ].map((s) => (
            <div key={s.n} className="glass rounded-xl p-6">
              <span className="font-mono-num text-emerald-500/60 text-3xl font-bold">{s.n}</span>
              <h3 className="font-semibold text-lg mt-3 mb-2">{s.t}</h3>
              <p className="text-slate-400 text-sm">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-3">Simple, transparent pricing</h2>
          <p className="text-slate-400">Start with a {config?.trial_days || 10}-day free trial. Upgrade anytime.</p>
          {config?.offer?.active && (
            <div className="inline-flex items-center gap-2 mt-4 bg-cyan-500/10 border border-cyan-500/20 text-cyan-300 px-4 py-1.5 rounded-full text-sm" data-testid="offer-banner">
              <Zap className="w-3.5 h-3.5" /> {config.offer.label}: {config.offer.text}
            </div>
          )}
        </div>
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {(config?.plans || []).map((p, i) => (
            <div key={p.id} data-testid={`pricing-card-${p.id}`}
              className={`glass rounded-2xl p-6 relative ${i === 1 ? "border-emerald-500/40 ring-1 ring-emerald-500/20" : ""}`}>
              {i === 1 && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-emerald-500 text-[#07090E] text-xs font-bold px-3 py-1 rounded-full">POPULAR</span>}
              <h3 className="font-sub font-bold text-xl mb-1">{p.name}</h3>
              <div className="flex items-baseline gap-1 mb-4">
                <span className="font-mono-num text-4xl font-extrabold">${p.price}</span>
                <span className="text-slate-500 text-sm">/{p.interval}</span>
              </div>
              <ul className="space-y-2 mb-6">
                {p.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2 text-sm text-slate-300">
                    <Check className="w-4 h-4 text-emerald-400 shrink-0 mt-0.5" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/login" data-testid={`pricing-cta-${p.id}`}
                className={`block text-center font-semibold px-4 py-2.5 rounded-full transition-colors ${
                  i === 1 ? "bg-emerald-500 hover:bg-emerald-400 text-[#07090E]" : "border border-white/15 hover:border-white/30 text-white"
                }`}>
                Start Free Trial
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Disclaimer + footer */}
      <footer className="border-t border-white/10 bg-[#0D111A]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
          <Disclaimer text={config?.disclaimer} />
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-slate-500">
            <div className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-500" />
              <span className="font-display font-bold text-white">AI Trading Copilot</span>
            </div>
            <p>© {new Date().getFullYear()} AI Trading Copilot. For educational purposes only.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
